import { Button, Typography, useAlert, useCalendar } from "matsuri-ui"
import { css } from "@emotion/react"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useId, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import dayjs from "dayjs"
import { FieldLikeButton } from "../components/FieldLikeButton"
import { SelectModalProvider, useSelectModal } from "../components/SelectModal"
import { requestCreateShift } from "../hooks/useShift"
import { ShiftCalendarPageUI } from "./ShiftPage/ShiftCalendarPageUI"
import { getTimeOptions } from "./ShiftPage/TimeField"
import { useShiftForm } from "./ShiftPage/ShiftForm"
import { displayWeekdayJa } from "@/utils/displayWeekday"
import { theme } from "@/theme"
import { useShift } from "@/hooks/useShifts"
import { useTranslation } from "@/hooks/useTranslation"

const startTimeLocalStorageKey = "shift.startTime"
const endTimeLocalStorageKey = "shift.endTime"

const RenderCalendarControls = () => {
    const { t } = useTranslation()
    return (
        <div
            css={css`
                display: grid;
                place-items: center;
                height: 100%;
                background-color: ${theme.palette.intentions.primary.light};
            `}
        >
            <Typography>{t("下書き作成中")}</Typography>
        </div>
    )
}

const CreateShift = ({ initialDate }: { initialDate?: string }) => {
    const { token } = useAuth()
    const { throwAlert } = useAlert()
    const navigate = useNavigate()
    const { t } = useTranslation()

    const startTimeInLocalStorage =
        localStorage.getItem(startTimeLocalStorageKey) ?? undefined
    const endTimeInLocalStorage =
        localStorage.getItem(endTimeLocalStorageKey) ?? undefined

    const { date, set } = useCalendar({ initialDate })

    const { data: shifts, refetch } = useShift(
        token,
        date.startOf("month"),
        date.endOf("month")
    )

    const { fieldNames, validate } = useShiftForm()

    // 初期値は10-16時に強制する
    // この時間に入れて欲しいという意図があるわけではなく、単にundefinedの時に何も表示されないと微妙なので、とにかく常に時刻が表示されるように適当な値を入れている
    const [startTime, setStartTime] = useState<string>(
        startTimeInLocalStorage
            ? dayjs.unix(Number(startTimeInLocalStorage)).format("HH:mm")
            : date.hour(10).minute(0).format("HH:mm")
    )
    const [endTime, setEndTime] = useState<string>(
        endTimeInLocalStorage
            ? dayjs.unix(Number(endTimeInLocalStorage)).format("HH:mm")
            : date.hour(16).minute(0).format("HH:mm")
    )

    const formId = useId()

    const { open } = useSelectModal()

    return (
        <ShiftCalendarPageUI
            date={date}
            onChangeDate={set}
            shifts={shifts}
            renderCalendarControls={RenderCalendarControls}
            renderContent={() => (
                <div
                    css={css`
                        padding: 16px 20px;
                    `}
                >
                    <form
                        id={formId}
                        onSubmit={async event => {
                            event.preventDefault()

                            const input = validate(
                                date.format("YYYY-MM-DD"),
                                startTime,
                                endTime
                            )
                            if (!input) {
                                return
                            }

                            const { error } = await requestCreateShift(token, {
                                ...input
                            })
                            throwAlert(error, {
                                errorMessage: {
                                    happend: t("作成に失敗しました")
                                },
                                successMessage: t("作成しました"),
                                onSuccess: () => {
                                    // 作成したら即座にページを切り替えるので、作成したシフトが読み込まれて欲しい
                                    refetch()

                                    // 作成に成功したら、同じ日付を選択した状態でシフト一覧に戻る
                                    navigate("/shift", {
                                        state: {
                                            date: date.format("YYYY-MM-DD")
                                        }
                                    })

                                    localStorage.setItem(
                                        startTimeLocalStorageKey,
                                        `${input.start}`
                                    )
                                    localStorage.setItem(
                                        endTimeLocalStorageKey,
                                        `${input.end}`
                                    )
                                }
                            })
                        }}
                        css={css`
                            display: flex;
                            flex-direction: column;
                            gap: 16px;
                        `}
                    >
                        <div>
                            <Typography variant="caption">
                                {t("日付")}
                            </Typography>
                            <Typography
                                css={css`
                                    font-size: 16px;
                                `}
                            >
                                {date.format("MM/DD")}
                                {`(${t(displayWeekdayJa(date.day()))})`}
                            </Typography>
                            {/* useShiftFormの制約のために必要 */}
                            <input
                                type="hidden"
                                value={date.format("YYYY-MM-DD")}
                                name={fieldNames.date}
                            />
                        </div>
                        <div
                            css={css`
                                display: flex;
                                gap: 16px;
                            `}
                        >
                            <FieldLikeButton
                                label={t("開始時刻")}
                                value={startTime}
                                onClick={async () => {
                                    const value = await open(
                                        getTimeOptions({
                                            max: endTime
                                        })
                                    )

                                    if (value) {
                                        setStartTime(value)
                                    }
                                }}
                            />
                            <FieldLikeButton
                                label={t("終了時刻")}
                                value={endTime}
                                onClick={async () => {
                                    const value = await open(
                                        getTimeOptions({
                                            min: startTime
                                        })
                                    )

                                    if (value) {
                                        setEndTime(value)
                                    }
                                }}
                            />
                        </div>
                    </form>
                </div>
            )}
            renderFooterContainer={() => (
                <div
                    css={css`
                        display: flex;
                        gap: 16px;
                        padding: 16px 20px 34px 20px;
                    `}
                >
                    <Button
                        type="button"
                        variant="filled"
                        onClick={() => {
                            navigate("/shift", {
                                state: { date: date.format("YYYY-MM-DD") }
                            })
                        }}
                    >
                        {t("キャンセル")}
                    </Button>
                    <Button
                        form={formId}
                        type="submit"
                        variant="filled"
                        color="primary"
                        fullWidth
                    >
                        {t("作成")}
                    </Button>
                </div>
            )}
        />
    )
}

export const NewShiftPage = () => {
    const { state } = useLocation()
    const stateTyped = state as { date?: string } | null

    return (
        <SelectModalProvider>
            <CreateShift initialDate={stateTyped?.date} />
        </SelectModalProvider>
    )
}
